import React, { Fragment, useState } from "react"
import styled from "@emotion/styled"
import { injectIntl } from "gatsby-plugin-intl"
import { graphql } from "gatsby"
import { useForm } from "react-hook-form"
import Footer from "../../components/Layout/Footer"
import { contactMailsend } from "../../utils/emailHelpers"
import LoaderSpin from "../../components/LoaderSpin"

import {
  SectionContainer,
  SectionInnerWrapper,
} from "../../components/Layout/styled"
import SeoComp from "../../components/SeoComp"
import ReactTooltip from "react-tooltip"
import { formSubmitEventScript } from "../../utils/additionalScriptHelpers"

const TitleBox = styled.div`
  position: relative;
  display: flex;
  margin-bottom: 14px;
  align-items: center;
`

const Title = styled.h1`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 28px;
  line-height: 1.5;
  color: #262626;
  font-family: "BebasNeueBold";
  font-weight: bold;
  margin-bottom: 0px;

  @media (max-width: 550px) {
    font-size: 24px;
  }
`

const OpenInfoBox = styled.div`
  position: relative;
  margin-left: 5px;
  width: 20px;

  .__react_component_tooltip {
    background: #fff;
    color: #262626;
    font-family: "Gotham Book";
    font-size: 14px;
    max-width: 300px;
    border-radius: 5px;
    padding: 20px;
  }

  .__react_component_tooltip p {
    margin: 0px;
    font-family: "Gotham Book";
    font-size: 14px;
  }

  .__react_component_tooltip.show {
    opacity: 1;
  }

  .__react_component_tooltip.place-left:before {
    border-left-color: #5db9bc;
  }

  .__react_component_tooltip.place-right:before {
    border-right-color: #5db9bc;
  }

  .__react_component_tooltip.place-top:before {
    border-top-color: #5db9bc;
  }

  .__react_component_tooltip.place-bottom:before {
    border-bottom-color: #5db9bc;
  }

  .__react_component_tooltip.place-left:after {
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    right: -6px;
    top: 50%;
    margin-top: -4px;
    border-left-color: #fff;
    border-left-style: solid;
    border-left-width: 6px;
  }

  .__react_component_tooltip.place-right:after {
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    left: -6px;
    top: 50%;
    margin-top: -4px;
    border-right-color: #fff;
    border-right-style: solid;
    border-right-width: 6px;
  }

  .__react_component_tooltip.place-top:after {
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    bottom: -6px;
    left: 50%;
    margin-left: -8px;
    border-top-color: #fff;
    border-top-style: solid;
    border-top-width: 6px;
  }

  .__react_component_tooltip.place-bottom:after {
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    top: -6px;
    left: 50%;
    margin-left: -8px;
    border-bottom-color: #fff;
    border-bottom-style: solid;
    border-bottom-width: 6px;
  }
`

const OpenInfo = styled.div`
  text-transform: lowercase;
  padding: 0px 0px;
  height: 17px;
  width: 17px;
  font-size: 13px;
  font-weight: bold;
  border-radius: 50%;
  border: 2px solid #47bebd;
  color: #47bebd;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const Subtitle = styled.h2`
  line-height: 1.5;
  font-size: 15px;
  color: #555;
  width: 100%;
  margin: 0px 0px 15px;
  letter-spacing: 0.05em;
  font-weight: 500;
  font-family: GothamLight;

  a {
    text-decoration: underline;
    white-space: nowrap;
  }
`

const Input = styled.input`
  background-color: #ede9e8;
  border-radius: 0;
  border: 0;
  padding: 0 12px;
  -webkit-appearance: none;
  box-sizing: border-box;
  height: 2.507em;
  font-size: 0.97em;
  width: 100%;
  vertical-align: middle;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: color 0.3s, border 0.3s, background 0.3s, opacity 0.3s;
  margin-bottom: 1em;
  touch-action: manipulation;
  overflow: visible;
  letter-spacing: normal;
  word-spacing: normal;
  text-transform: none;
  text-indent: 0px;
  text-shadow: none;
  display: inline-block;
  text-align: start;
  cursor: text;
  font-family: GothamLight;
  margin-top: 0px;
  color: #0e1116;
  ::placeholder {
    color: #0e1116;
  }

  :focus {
    background-color: #ede9e8;
  }
`

const Message = styled.textarea`
  height: 200px;
  min-height: 120px;
  background-color: #ede9e8;
  border-radius: 0;
  border: 0;
  padding: 0 12px;
  margin-bottom: 1em;
  min-height: 120px;
  -webkit-appearance: none;
  box-sizing: border-box;
  font-size: 0.97em;
  max-width: 100%;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: color 0.3s, border 0.3s, background 0.3s, opacity 0.3s;
  overflow: auto;
  letter-spacing: normal;
  word-spacing: normal;
  text-transform: none;
  text-indent: 0px;
  text-shadow: none;
  display: inline-block;
  text-align: start;
  -webkit-rtl-ordering: logical;
  flex-direction: column;
  resize: auto;
  cursor: text;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  font-family: GothamLight;
  outline: none;
  width: 100%;

  :focus {
    background-color: #ede9e8;
  }
`

const UploadFileInput = styled.input`
  display: block;
  margin: 10px 0px 20px;
  padding: 0px;
  border: 0px;
  box-shadow: none;
`

const Submit = styled.button`
  position: relative;
  display: inline-block;
  color: #fff;
  border-color: rgba(0, 0, 0, 0.05);
  background-color: #262626;
  text-transform: uppercase;
  font-size: 0.97em;
  letter-spacing: 0.03em;
  touch-action: none;
  cursor: pointer;
  font-weight: bolder;
  text-align: center;
  text-decoration: none;
  border: 1px solid transparent;
  vertical-align: middle;
  border-radius: 0;
  margin-top: 0;
  margin-right: 1em;
  text-shadow: none;
  line-height: 2.4em;
  min-height: 2.5em;
  padding: 0 1.2em;
  max-width: 100%;
  transition: transform 0.3s, border 0.3s, background 0.3s, box-shadow 0.3s,
    opacity 0.3s, color 0.3s;
  text-rendering: optimizeLegibility;
  box-sizing: border-box;
  -webkit-appearance: button;
  margin-bottom: 1em;
  overflow: visible;
  font-family: GothamLight;
  width: fit-content;
  outline: none;
`

const ErrorSpan = styled.span`
  margin-top: -5px;
  position: relative;
  padding: 5px 8px;
  line-height: 1.2em;
  border-radius: 3px;
  opacity: 0.8;
  background-color: #f1f1f1;
  color: #b20000;
  font-size: 1em;
  font-weight: 400;
  display: block;
  box-sizing: border-box;
  letter-spacing: 0;
  font-family: GothamLight;
  margin-bottom: 10px;

  ::after {
    bottom: 100%;
    left: 30px;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: transparent;
    border-bottom-color: #f1f1f1;
    border-width: 10px;
    margin-left: -10px;
    box-sizing: border-box;
  }
`

const SuccessBox = styled.div`
  display: block;
  box-sizing: border-box;
  color: #398f14;
  line-height: 1.6;
  font-size: 14px;
  font-weight: 400;
  font-family: "GothamLight";

  @media (max-width: 550px) {
    padding-bottom: 20px;
  }
`

const ErrorBox = styled.div`
  display: block;
  box-sizing: border-box;
  color: #b20000;
  line-height: 1.6;
  font-size: 14px;
  font-weight: 400;
  font-family: "GothamLight";

  @media (max-width: 550px) {
    padding-bottom: 20px;
  }
`

const Form = styled.form`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  margin-bottom: 10px;
`

const Label = styled.h2`
  max-width: 100%;
  margin-bottom: 5px;
  font-family: "GothamLight";
  font-weight: bold;
  font-size: 12.6px;
  line-height: 14px;
  color: #47bebd;
  display: flex;
  align-items: center;

  > span {
    color: #ee0000;
  }

  > div {
    font-weight: 500;
  }
`

const Select = styled.select`
  min-height: 30px;
  background: #444;
  border-width: 1px;
  border-style: solid;
  background-color: #ede9e8;
  border-radius: 0;
  border: 0;
  padding: 0 12px;
  box-sizing: border-box;
  height: 2.507em;
  font-size: 0.97em;
  width: 100%;
  vertical-align: middle;
  box-shadow: inset 0 1px 2px rgb(0 0 0 / 10%);
  transition: color 0.3s, border 0.3s, background 0.3s, opacity 0.3s;
  margin-bottom: 1em;
  cursor: pointer;
  font-family: GothamLight;
  margin-top: 0px;
  color: #0e1116;
`

const Row = styled.div`
  width: 100%;
`

const ColumnRow = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 15px;

  @media (max-width: 550px) {
    flex-direction: column;
  }
`

const Column = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;

  input {
    margin: 0px 5px 0px 0px;
    display: inline;
    cursor: pointer;
  }

  label {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  :nth-of-type(2) {
    margin-left: 50px;

    @media (max-width: 550px) {
      margin-left: 0px;
    }
  }
`

const contact = ({ data, intl }) => {
  const { register, errors, handleSubmit, reset } = useForm()
  const [successSubmit, setsuccessSubmit] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [fileError, setFileError] = useState("")
  const [loading, setloading] = useState(false)
  const [isCustomer, setisCustomer] = useState("customer")
  const [files, setFiles] = useState([])
  const onSubmit = async data => {
    setloading(true)

    const formData = new FormData()

    if (data.files && data.files.length > 0) {
      const selectedFiles = data.files
      Array.from(selectedFiles).forEach(file => {
        formData.append("files", file)
      })
    }

    for (const key of Object.keys(data)) {
      if (key != "files") {
        formData.append(key, data[key])
      }
    }
    let send = await contactMailsend(formData, setloading)
    if (send.status === "success") {
      reset({ name: "", email: "", subject: "", message: "" })
      formSubmitEventScript("contact_submit", intl.locale)
      setErrorMessage("")
      setsuccessSubmit(true)
    } else {
      console.log("Error", send.message)
      setsuccessSubmit(false)
      setErrorMessage(send.message)
    }
    setloading(false)
  }
  let re = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  )

  const handleFileChange = event => {
    let maxFileUploadExceed = false
    let maxSizeLimitExceed = false
    let fileTypeError = false
    const selectedFiles = Array.from(event.target.files)
    const allowedFileTypes = ["pdf", "gif", "jpeg", "jpg", "png"]

    if (selectedFiles.length > 1) maxFileUploadExceed = true

    selectedFiles.forEach(file => {
      let fileExtension = file.name.split(".").at(-1)
      if (file.size > 3.5 * 1024 * 1024) maxSizeLimitExceed = true
      if (!allowedFileTypes.includes(fileExtension)) fileTypeError = true
    })

    setFileError("")

    if (maxFileUploadExceed) {
      setFileError("Vous ne pouvez télécharger qu'un seul fichier.")
      event.target.value = null
    } else if (maxSizeLimitExceed) {
      setFileError("Veuillez télécharger un fichier de moins de 3.5 Mo.")
      event.target.value = null
    } else if (fileTypeError) {
      setFileError(
        `Veuillez télécharger un fichier ${allowedFileTypes.join(", ")}`
      )
      event.target.value = null
    } else {
      setFiles(selectedFiles)
    }
  }
  return (
    data &&
    data.allDirectusContactPageTranslation.nodes[0] && (
      <Fragment>
        <SeoComp
          description={
            data.allDirectusSeoTranslation.nodes[0] &&
            data.allDirectusSeoTranslation.nodes[0].meta_description
          }
          title={
            data.allDirectusSeoTranslation.nodes[0] &&
            data.allDirectusSeoTranslation.nodes[0].meta_title
          }
          twitter_card={
            data.allDirectusSeoTranslation.nodes[0] &&
            data.allDirectusSeoTranslation.nodes[0].twitter_card
          }
          image={
            data.allDirectusSeoTranslation.nodes[0] &&
            data.allDirectusSeoTranslation.nodes[0].image &&
            data.allDirectusSeoTranslation.nodes[0].image.data &&
            data.allDirectusSeoTranslation.nodes[0].image.data.url
          }
          hreflangPathname={{
            fr: "contact",
            en: "contact",
            de: "contact",
            es: "contact",
          }}
        />
        <SectionContainer>
          <SectionInnerWrapper align="flex-start">
            {data.allDirectusContactPageTranslation.nodes[0].title && (
              <TitleBox>
                <Title>
                  {data.allDirectusContactPageTranslation.nodes[0].title}
                </Title>
                {data.allDirectusContactPageTranslation.nodes[0]
                  .title_tooltip && (
                  <OpenInfoBox>
                    <OpenInfo
                      data-tip={
                        data.allDirectusContactPageTranslation.nodes[0]
                          .title_tooltip
                      }
                      data-for="title"
                    >
                      i
                    </OpenInfo>
                    <ReactTooltip id="title" />
                  </OpenInfoBox>
                )}
              </TitleBox>
            )}
            {data.allDirectusContactPageTranslation.nodes[0].subtitle && (
              <div style={{ display: "inline", marginBottom: 15 }}>
                <Subtitle
                  dangerouslySetInnerHTML={{
                    __html:
                      data.allDirectusContactPageTranslation.nodes[0].subtitle,
                  }}
                  style={{ display: "inline" }}
                />
                {data.allDirectusContactPageTranslation.nodes[0]
                  .phone_tooltip && (
                  <OpenInfoBox style={{ display: "inline" }}>
                    <OpenInfo
                      data-tip={
                        data.allDirectusContactPageTranslation.nodes[0]
                          .phone_tooltip
                      }
                      data-for="phone"
                    >
                      i
                    </OpenInfo>
                    <ReactTooltip id="phone" />
                  </OpenInfoBox>
                )}
              </div>
            )}
            {/* <div style={{ display: "inline", marginBottom: 15 }}>
              <Subtitle
                dangerouslySetInnerHTML={{
                  __html:
                    intl.locale === "es"
                      ? "<a href='mailto:contact@percko.com'>contact@percko.com</a>"
                      : intl.locale === "de"
                      ? "<a href='mailto:contact@percko.com'>contact@percko.com</a>"
                      : intl.locale === "en"
                      ? "You can contact us by emailing <a href='mailto:contact@percko.com'>contact@percko.com</a> or by calling us: <a href='tel:+4402034993675'>+44 (0) 20 3499 3675</a>"
                      : "VOUS POUVEZ NOUS CONTACTER À L'ADRESSE E-MAIL SUIVANTE <a href='mailto:contact@percko.com'>CONTACT@PERCKO.COM</a>",
                }}
                style={{ display: "inline" }}
              />
              {data.allDirectusContactPageTranslation.nodes[0]
                .phone_tooltip && (
                <OpenInfoBox style={{ display: "inline" }}>
                  <OpenInfo
                    data-tip={
                      data.allDirectusContactPageTranslation.nodes[0]
                        .phone_tooltip
                    }
                    data-for="phone"
                  >
                    i
                  </OpenInfo>
                  <ReactTooltip id="phone" />
                </OpenInfoBox>
              )}
            </div> */}
            <Form
              onSubmit={handleSubmit(onSubmit)}
              encType="multipart/form-data"
            >
              {(intl.locale === "fr" || intl.locale === "en") && (
                <ColumnRow>
                  {data.allDirectusContactPageTranslation.nodes[0].radio_button_text.list.map(
                    (item, index) => {
                      return (
                        item.value &&
                        item.text && (
                          <Column key={index.toString()}>
                            <label>
                              <input
                                type="radio"
                                name="customers"
                                ref={register({
                                  required: true,
                                })}
                                checked={isCustomer === item.value}
                                value={item.value}
                                onChange={e => {
                                  setisCustomer(e.target.value)
                                }}
                              />
                              {item.text}
                            </label>
                          </Column>
                        )
                      )
                    }
                  )}
                </ColumnRow>
              )}
              {errors && errors.customers && (
                <ErrorSpan>
                  {errors.customers.type === "required"
                    ? data.allDirectusContactPageTranslation.nodes[0]
                        .require_error_text
                    : data.allDirectusContactPageTranslation.nodes[0]
                        .field_error_text}
                </ErrorSpan>
              )}
              {(intl.locale === "fr" || intl.locale === "en") && (
                <Row>
                  <Label>
                    {
                      data.allDirectusContactPageTranslation.nodes[0]
                        .last_name_label_text
                    }
                    <span>*</span>
                    {data.allDirectusContactPageTranslation.nodes[0]
                      .last_name_tooltip && (
                      <OpenInfoBox>
                        <OpenInfo
                          data-tip={
                            data.allDirectusContactPageTranslation.nodes[0]
                              .last_name_tooltip
                          }
                          data-for="lastname"
                        >
                          i
                        </OpenInfo>
                        <ReactTooltip id="lastname" />
                      </OpenInfoBox>
                    )}
                  </Label>
                  <Input
                    name="lname"
                    ref={register({
                      required: true,
                      pattern: /^[a-zA-z]+([\s\\_-][a-zA-Z]+)*$/i,
                      maxLength: 20,
                    })}
                    aria-invalid={false}
                  />
                  {errors && errors.lname && (
                    <ErrorSpan>
                      {errors.lname.type === "required"
                        ? data.allDirectusContactPageTranslation.nodes[0]
                            .require_error_text
                        : data.allDirectusContactPageTranslation.nodes[0]
                            .field_error_text}
                    </ErrorSpan>
                  )}
                </Row>
              )}
              {(intl.locale === "fr" || intl.locale === "en") && (
                <Row>
                  <Label>
                    {
                      data.allDirectusContactPageTranslation.nodes[0]
                        .first_name_label_text
                    }
                    <span>*</span>
                    {data.allDirectusContactPageTranslation.nodes[0]
                      .first_name_tooltip && (
                      <OpenInfoBox>
                        <OpenInfo
                          data-tip={
                            data.allDirectusContactPageTranslation.nodes[0]
                              .first_name_tooltip
                          }
                          data-for="firstname"
                        >
                          i
                        </OpenInfo>
                        <ReactTooltip id="firstname" />
                      </OpenInfoBox>
                    )}
                  </Label>
                  <Input
                    name="fname"
                    ref={register({
                      required: true,
                      pattern: /^[a-zA-z]+([\s\\_-][a-zA-Z]+)*$/i,
                      maxLength: 20,
                    })}
                    aria-invalid={false}
                  />
                  {errors && errors.fname && (
                    <ErrorSpan>
                      {errors.fname.type === "required"
                        ? data.allDirectusContactPageTranslation.nodes[0]
                            .require_error_text
                        : data.allDirectusContactPageTranslation.nodes[0]
                            .field_error_text}
                    </ErrorSpan>
                  )}
                </Row>
              )}
              {(intl.locale === "de" || intl.locale === "es") && (
                <Row>
                  <Label>
                    {
                      data.allDirectusContactPageTranslation.nodes[0]
                        .name_label_text
                    }
                    <span>*</span>
                    {data.allDirectusContactPageTranslation.nodes[0]
                      .name_tooltip && (
                      <OpenInfoBox>
                        <OpenInfo
                          data-tip={
                            data.allDirectusContactPageTranslation.nodes[0]
                              .name_tooltip
                          }
                          data-for="fname"
                        >
                          i
                        </OpenInfo>
                        <ReactTooltip id="fname" />
                      </OpenInfoBox>
                    )}
                  </Label>
                  <Input
                    name="name"
                    ref={register({
                      required: true,
                      pattern: /^[a-zA-z]+([\s\\_-][a-zA-Z]+)*$/i,
                      maxLength: 20,
                    })}
                    aria-invalid={false}
                  />
                  {errors && errors.name && (
                    <ErrorSpan>
                      {errors.name.type === "required"
                        ? data.allDirectusContactPageTranslation.nodes[0]
                            .require_error_text
                        : data.allDirectusContactPageTranslation.nodes[0]
                            .field_error_text}
                    </ErrorSpan>
                  )}
                </Row>
              )}
              <Input
                type="hidden"
                name="lang"
                ref={register({ required: true })}
                aria-invalid={false}
                value={intl.locale}
              />
              <Row>
                <Label>
                  {
                    data.allDirectusContactPageTranslation.nodes[0]
                      .email_label_text
                  }
                  <span>*</span>
                  {data.allDirectusContactPageTranslation.nodes[0]
                    .email_tooltip && (
                    <OpenInfoBox>
                      <OpenInfo
                        data-tip={
                          data.allDirectusContactPageTranslation.nodes[0]
                            .email_tooltip
                        }
                        data-for="email"
                      >
                        i
                      </OpenInfo>
                      <ReactTooltip id="email" />
                    </OpenInfoBox>
                  )}
                </Label>
                <Input
                  name="email"
                  ref={register({
                    required: true,
                    pattern: re,
                  })}
                  aria-invalid={false}
                />
                {errors && errors.email && (
                  <ErrorSpan>
                    {errors.email.type === "pattern"
                      ? data.allDirectusContactPageTranslation.nodes[0]
                          .email_error_text
                      : data.allDirectusContactPageTranslation.nodes[0]
                          .require_error_text}
                  </ErrorSpan>
                )}
              </Row>
              {isCustomer &&
                isCustomer === "customer" &&
                (intl.locale === "fr" || intl.locale === "en") && (
                  <Row>
                    <Label>
                      {
                        data.allDirectusContactPageTranslation.nodes[0]
                          .order_label_text
                      }
                      <span>*</span>
                      {data.allDirectusContactPageTranslation.nodes[0]
                        .order_tooltip && (
                        <OpenInfoBox>
                          <OpenInfo
                            data-tip={
                              data.allDirectusContactPageTranslation.nodes[0]
                                .order_tooltip
                            }
                            data-for="order"
                          >
                            i
                          </OpenInfo>
                          <ReactTooltip id="order" />
                        </OpenInfoBox>
                      )}
                    </Label>
                    <Input
                      name="order"
                      ref={register({
                        required: false,
                        pattern: /^[0-9]+$/i,
                        min: 999,
                        max: 99999999,
                      })}
                      aria-invalid={false}
                    />
                    {errors && errors.order && (
                      <ErrorSpan>
                        {errors.order.type === "required"
                          ? data.allDirectusContactPageTranslation.nodes[0]
                              .require_error_text
                          : data.allDirectusContactPageTranslation.nodes[0]
                              .require_error_text}
                      </ErrorSpan>
                    )}
                  </Row>
                )}
              {(intl.locale === "fr" || intl.locale === "en") && (
                <Row>
                  <Label>
                    {
                      data.allDirectusContactPageTranslation.nodes[0]
                        .reason_label_text
                    }
                    <span>*</span>
                  </Label>
                  {isCustomer &&
                    isCustomer === "customer" &&
                    data.allDirectusContactPageTranslation.nodes[0]
                      .customer_reason_list && (
                      <Select
                        name="reason"
                        ref={register({
                          required: true,
                        })}
                      >
                        <option value="">
                          {
                            data.allDirectusContactPageTranslation.nodes[0]
                              .reason_label_text
                          }
                        </option>
                        {data.allDirectusContactPageTranslation.nodes[0].customer_reason_list.list.map(
                          (item, index) => {
                            return (
                              item.val &&
                              item.text && (
                                <option key={index.toString()} value={item.val}>
                                  {item.text}
                                </option>
                              )
                            )
                          }
                        )}
                      </Select>
                    )}
                  {isCustomer &&
                    isCustomer === "not_customer" &&
                    data.allDirectusContactPageTranslation.nodes[0]
                      .not_customer_reason_list && (
                      <Select
                        name="reason"
                        ref={register({
                          required: true,
                        })}
                      >
                        <option value="">
                          {
                            data.allDirectusContactPageTranslation.nodes[0]
                              .reason_label_text
                          }
                        </option>
                        {data.allDirectusContactPageTranslation.nodes[0].not_customer_reason_list.list.map(
                          (item, index) => {
                            return (
                              item.val &&
                              item.text && (
                                <option key={index.toString()} value={item.val}>
                                  {item.text}
                                </option>
                              )
                            )
                          }
                        )}
                      </Select>
                    )}
                  {errors && errors.reason && (
                    <ErrorSpan>
                      {errors.reason.type === "required"
                        ? data.allDirectusContactPageTranslation.nodes[0]
                            .require_error_text
                        : ""}
                    </ErrorSpan>
                  )}
                </Row>
              )}
              {(intl.locale === "de" || intl.locale === "es") && (
                <Row>
                  <Label>
                    {
                      data.allDirectusContactPageTranslation.nodes[0]
                        .subject_label_text
                    }
                    <span>*</span>
                  </Label>
                  <Input
                    name="subject"
                    ref={register({
                      required: true,
                    })}
                    aria-invalid={false}
                  />
                  {errors && errors.subject && (
                    <ErrorSpan>
                      {errors.subject.type === "required"
                        ? data.allDirectusContactPageTranslation.nodes[0]
                            .require_error_text
                        : ""}
                    </ErrorSpan>
                  )}
                </Row>
              )}
              <Row>
                <Label>
                  {
                    data.allDirectusContactPageTranslation.nodes[0]
                      .message_label_text
                  }
                  <span>*</span>
                </Label>
                <Message
                  cols={40}
                  rows={8}
                  name="message"
                  ref={register({
                    required: true,
                  })}
                />
                {errors && errors.message && (
                  <ErrorSpan>
                    {errors.message.type === "required"
                      ? data.allDirectusContactPageTranslation.nodes[0]
                          .require_error_text
                      : ""}
                  </ErrorSpan>
                )}
              </Row>
              {process.env.GATSBY_ACTIVE_ENV === "eur" && intl.locale === "fr" && (
                <Row>
                  <Label>
                    Joindre un fichier (maximum 3.5 Mo, .jpg, .gif, .png, .pdf
                    autorisés)
                  </Label>
                  <UploadFileInput
                    type="file"
                    name="files"
                    ref={register({ required: false })}
                    accept="application/pdf, image/gif, image/jpeg, image/jpg, image/png"
                    onChange={handleFileChange}
                  />
                  {fileError && (
                    <ErrorSpan
                      dangerouslySetInnerHTML={{
                        __html: fileError,
                      }}
                    />
                  )}
                </Row>
              )}
              <Submit type="submit">
                {data.allDirectusContactPageTranslation.nodes[0].button_text}
              </Submit>
            </Form>
            {successSubmit && (
              <SuccessBox
                dangerouslySetInnerHTML={{
                  __html:
                    data.allDirectusContactPageTranslation.nodes[0]
                      .email_sent_success,
                }}
              />
            )}
            {errorMessage && (
              <ErrorBox
                dangerouslySetInnerHTML={{
                  __html: errorMessage,
                }}
              />
            )}
          </SectionInnerWrapper>
        </SectionContainer>

        {data.allDirectusFooterTranslation.nodes.length > 0 && (
          <Footer data={data.allDirectusFooterTranslation.nodes[0]} />
        )}

        <LoaderSpin loading={loading} />
      </Fragment>
    )
  )
}

export const query = graphql`
  query($lang: String!) {
    allDirectusContactPageTranslation(filter: { language: { eq: $lang } }) {
      nodes {
        title
        title_tooltip
        subtitle
        phone_tooltip
        name_label_text
        name_tooltip
        email_label_text
        subject_label_text
        message_label_text
        button_text
        require_error_text
        email_error_text
        field_error_text
        email_sent_success
        radio_button_text {
          list {
            text
            value
          }
        }
        last_name_label_text
        last_name_tooltip
        first_name_label_text
        first_name_tooltip
        email_tooltip
        order_label_text
        order_tooltip
        reason_label_text
        customer_reason_list {
          list {
            text
            val
          }
        }
        not_customer_reason_list {
          list {
            text
            val
          }
        }
      }
    }
    allDirectusFooterTranslation(filter: { language: { eq: $lang } }) {
      nodes {
        facebook
        gbp_facebook
        youtube
        twitter
        instagram
        linkedin
        about_us {
          name
          list {
            link
            list
          }
        }
        contact {
          name
          list {
            link
            list
          }
          button {
            link
            list
          }
        }
        purchases {
          list {
            list
            link
          }
          name
        }
        help {
          list {
            link
            list
          }
          name
        }
        about_us_menu {
          name
          list {
            link
            list
          }
        }
        knowledge_center_menu {
          name
          list {
            link
            list
          }
        }
        shop_menu {
          name
          list {
            link
            list
          }
        }
        support_menu {
          name
          list {
            link
            list
          }
          button {
            link
            list
          }
        }
        newsletter_heading
        follow_us_heading
        footer_bottom {
          list {
            link
            list
          }
        }
      }
    }
    allDirectusSeoTranslation(
      filter: { language: { eq: $lang }, seo: { page_name: { eq: "contact" } } }
    ) {
      nodes {
        meta_title
        meta_description
        twitter_card
        twitter_title
        url
        image {
          data {
            url
          }
        }
      }
    }
  }
`

export default injectIntl(contact)
